import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Save,
  Plus,
  X,
  Upload,
  ExternalLink,
  Store,
  Palette,
  Globe,
  Phone,
  ShieldCheck,
  LayoutDashboard,
  Sparkles,
  ChevronRight,
} from "lucide-react";
import { api, API, getToken } from "../../../lib/auth";
import { useParams } from "react-router-dom";

function Card({ title, desc, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
      <div className="px-6 lg:px-8 py-5 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-2xl bg-emerald-50 flex items-center justify-center">
            <Icon size={20} className="text-emerald-600" />
          </div>

          <div>
            <h2 className="text-lg font-bold text-slate-900">{title}</h2>
            <p className="text-sm text-slate-500 mt-1">{desc}</p>
          </div>
        </div>
      </div>

      <div className="p-6 lg:p-8">{children}</div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div className="space-y-2">
      <label className="text-sm font-semibold text-slate-800 block">
        {label}
      </label>

      {children}

      {hint && (
        <p className="text-xs text-slate-500 leading-relaxed">{hint}</p>
      )}
    </div>
  );
}

function Input({
  value,
  onChange,
  type = "text",
  placeholder,
  testid,
}) {
  return (
    <input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full h-12 px-4 rounded-2xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all"
    />
  );
}

function Textarea({
  value,
  onChange,
  rows = 4,
  placeholder,
  testid,
}) {
  return (
    <textarea
      rows={rows}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-4 py-3 rounded-2xl border border-slate-200 bg-white text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500 transition-all resize-none"
    />
  );
}

function Toggle({ value, onChange, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onChange(!value)}
      className={`relative inline-flex h-7 w-14 items-center rounded-full transition-all duration-300 ${
        value ? "bg-emerald-500" : "bg-slate-300"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white transition-all duration-300 ${
          value ? "translate-x-8" : "translate-x-1"
        }`}
      />
    </button>
  );
}

function FileInput({ url, onChange, kind, testid }) {
  const [busy, setBusy] = useState(false);

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;

    setBusy(true);

    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("file", f);

      const r = await fetch(`${API}/t/store/upload`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
        body: fd,
      });

      const j = await r.json();

      if (j.url) {
        onChange(j.url);
        toast.success("File uploaded successfully");
      } else {
        toast.error(j.detail || "Upload failed");
      }
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <label
        className={`flex-1 border-2 border-dashed border-slate-200 rounded-2xl p-4 bg-slate-50 hover:bg-slate-100 transition cursor-pointer ${
          busy ? "opacity-60 pointer-events-none" : ""
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-medium text-sm text-slate-700 truncate">
              {url ? url.split("/").pop() : "Choose image"}
            </div>

            <div className="text-xs text-slate-500 mt-1">
              PNG, JPG or WEBP
            </div>
          </div>

          <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 flex items-center justify-center">
            <Upload size={16} className="text-slate-600" />
          </div>
        </div>

        <input
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPick}
          disabled={busy}
          data-testid={testid}
        />
      </label>

      {url && (
        <div className="w-20 h-20 rounded-2xl overflow-hidden border border-slate-200 bg-slate-100">
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}${url}`}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      )}
    </div>
  );
}

export default function StoreSettings() {
  const { subdomain } = useParams();

  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/t/store/settings").then((r) => setS(r.data));
  }, []);

  const upd = (patch) => {
    setS((prev) => ({
      ...prev,
      ...patch,
    }));
  };

  const save = async () => {
    setBusy(true);

    try {
      await api.patch("/t/store/settings", s);
      toast.success("Settings saved successfully");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    } finally {
      setBusy(false);
    }
  };

  if (!s) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-slate-500 text-sm">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto p-4 lg:p-8 space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-800 rounded-3xl p-8 lg:p-10 text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-72 h-72 bg-white/5 rounded-full blur-3xl" />

          <div className="relative z-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-sm font-medium mb-4">
                <Sparkles size={14} />
                Premium Store Settings
              </div>

              <h1 className="text-4xl font-black tracking-tight">
                Store Configuration
              </h1>

              <p className="text-slate-300 mt-3 max-w-2xl leading-relaxed">
                Manage your store branding, appearance, SEO, customer
                experience and online visibility from one place.
              </p>
            </div>

            <div className="flex items-center gap-3">
              <a
                href={`/store/${subdomain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl bg-white/10 hover:bg-white/20 transition border border-white/10 font-semibold"
              >
                <ExternalLink size={16} />
                Visit Store
              </a>

              <button
                onClick={save}
                disabled={busy}
                data-testid="ss-save"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-emerald-500 hover:bg-emerald-600 transition text-white font-bold shadow-lg shadow-emerald-500/20 disabled:opacity-60"
              >
                <Save size={16} />
                {busy ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>

        {/* Store Basics */}
        <Card
          title="Store Basics"
          desc="Basic store setup and configuration"
          icon={Store}
        >
          <div className="grid lg:grid-cols-3 gap-6">
            <Field
              label="Online Store"
              hint="Enable or disable your public storefront"
            >
              <div className="flex items-center justify-between border border-slate-200 rounded-2xl px-4 py-3">
                <div>
                  <div className="font-medium text-sm text-slate-800">
                    Store Status
                  </div>

                  <div className="text-xs text-slate-500 mt-1">
                    Public access to store
                  </div>
                </div>

                <Toggle
                  value={s.online_store_enabled}
                  onChange={(v) =>
                    upd({ online_store_enabled: v })
                  }
                  testid="ss-enabled"
                />
              </div>
            </Field>

            <Field label="Store Name">
              <Input
                value={s.store_name}
                onChange={(v) => upd({ store_name: v })}
                placeholder="Enter store name"
                testid="ss-name"
              />
            </Field>

            <Field
              label="Currency"
              hint="Changing currency affects your entire store"
            >
              <select
                value={s.currency}
                onChange={(e) =>
                  upd({ currency: e.target.value })
                }
                data-testid="ss-currency"
                className="w-full h-12 px-4 rounded-2xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-4 focus:ring-emerald-100 focus:border-emerald-500"
              >
                <option value="NGN">Naira (₦)</option>
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
              </select>
            </Field>

            <Field label="Default Warehouse">
              <Input
                value={s.default_warehouse}
                onChange={(v) =>
                  upd({ default_warehouse: v })
                }
                placeholder="Warehouse name"
                testid="ss-warehouse"
              />
            </Field>

            <Field label="Font Family">
              <Input
                value={s.font_family}
                onChange={(v) => upd({ font_family: v })}
                placeholder="Inter, Poppins, etc"
                testid="ss-font"
              />
            </Field>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-5">
              <Palette size={18} className="text-emerald-600" />
              <h3 className="font-bold text-slate-900">
                Brand Colors
              </h3>
            </div>

            <div className="grid lg:grid-cols-2 gap-6">
              <Field label="Primary Color">
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={s.primary_color}
                    onChange={(e) =>
                      upd({ primary_color: e.target.value })
                    }
                    className="w-16 h-12 rounded-2xl border border-slate-200 cursor-pointer bg-white"
                  />

                  <div className="flex-1">
                    <Input
                      value={s.primary_color}
                      onChange={(v) =>
                        upd({ primary_color: v })
                      }
                    />
                  </div>
                </div>
              </Field>

              <Field label="Secondary Color">
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={s.secondary_color}
                    onChange={(e) =>
                      upd({ secondary_color: e.target.value })
                    }
                    className="w-16 h-12 rounded-2xl border border-slate-200 cursor-pointer bg-white"
                  />

                  <div className="flex-1">
                    <Input
                      value={s.secondary_color}
                      onChange={(v) =>
                        upd({ secondary_color: v })
                      }
                    />
                  </div>
                </div>
              </Field>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-100">
            <div className="flex items-center gap-2 mb-5">
              <ShieldCheck
                size={18}
                className="text-emerald-600"
              />
              <h3 className="font-bold text-slate-900">
                Store Behaviour
              </h3>
            </div>

            <div className="space-y-4">
              {[
                {
                  label: "Allow Overselling",
                  desc: "Customers can buy products even when stock is unavailable",
                  value: s.allow_overselling,
                  key: "allow_overselling",
                },
                {
                  label: "Hide Out of Stock Products",
                  desc: "Products with zero quantity will not be visible",
                  value: s.hide_out_of_stock,
                  key: "hide_out_of_stock",
                },
                {
                  label: "Hide Prices For Guests",
                  desc: "Only logged-in customers can see prices",
                  value: s.hide_prices_for_guests,
                  key: "hide_prices_for_guests",
                },
              ].map((item) => (
                <div
                  key={item.key}
                  className="flex items-center justify-between gap-4 border border-slate-200 rounded-2xl p-4"
                >
                  <div>
                    <div className="font-semibold text-slate-800">
                      {item.label}
                    </div>

                    <div className="text-sm text-slate-500 mt-1">
                      {item.desc}
                    </div>
                  </div>

                  <Toggle
                    value={item.value}
                    onChange={(v) =>
                      upd({ [item.key]: v })
                    }
                  />
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Contact */}
        <Card
          title="Contact Information"
          desc="Store contact details visible to customers"
          icon={Phone}
        >
          <div className="grid lg:grid-cols-3 gap-6">
            <Field label="Contact Email">
              <Input
                type="email"
                value={s.contact_email}
                onChange={(v) =>
                  upd({ contact_email: v })
                }
                placeholder="hello@store.com"
              />
            </Field>

            <Field label="Contact Phone">
              <Input
                value={s.contact_phone}
                onChange={(v) =>
                  upd({ contact_phone: v })
                }
                placeholder="+234..."
              />
            </Field>

            <Field label="Store Address">
              <Input
                value={s.contact_address}
                onChange={(v) =>
                  upd({ contact_address: v })
                }
                placeholder="Enter address"
              />
            </Field>
          </div>
        </Card>

        {/* Branding */}
        <Card
          title="Branding Assets"
          desc="Upload your store logo and favicon"
          icon={Palette}
        >
          <div className="grid lg:grid-cols-2 gap-8">
            <Field label="Store Logo">
              <FileInput
                url={s.logo_url}
                onChange={(u) => upd({ logo_url: u })}
                kind="logo"
                testid="ss-logo"
              />
            </Field>

            <Field label="Favicon">
              <FileInput
                url={s.favicon_url}
                onChange={(u) =>
                  upd({ favicon_url: u })
                }
                kind="favicon"
                testid="ss-favicon"
              />
            </Field>
          </div>
        </Card>

        {/* Hero */}
        <Card
          title="Homepage Hero"
          desc="Customize your homepage hero section"
          icon={LayoutDashboard}
        >
          <div className="space-y-6">
            <Field label="Hero Title">
              <Input
                value={s.hero_title}
                onChange={(v) => upd({ hero_title: v })}
                placeholder="Welcome to our store"
              />
            </Field>

            <Field label="Hero Subtitle">
              <Textarea
                value={s.hero_subtitle}
                onChange={(e) =>
                  upd({ hero_subtitle: e.target.value })
                }
                placeholder="Write a short and engaging subtitle"
              />
            </Field>

            <Field label="Hero Image">
              <FileInput
                url={s.hero_image}
                onChange={(u) => upd({ hero_image: u })}
                kind="hero"
                testid="ss-hero"
              />
            </Field>
          </div>
        </Card>

        {/* SEO */}
        <Card
          title="SEO Settings"
          desc="Improve your store visibility on search engines"
          icon={Globe}
        >
          <div className="space-y-6">
            <Field label="SEO Title">
              <Input
                value={s.seo_title}
                onChange={(v) => upd({ seo_title: v })}
                placeholder="SEO title"
              />
            </Field>

            <Field label="SEO Description">
              <Textarea
                value={s.seo_description}
                onChange={(e) =>
                  upd({ seo_description: e.target.value })
                }
                placeholder="SEO description"
              />
            </Field>
          </div>
        </Card>

        {/* Topbar & Footer */}
        <Card
          title="Topbar & Footer"
          desc="Manage website notices and footer content"
          icon={ChevronRight}
        >
          <div className="grid lg:grid-cols-2 gap-6">
            <Field label="Topbar Left Text">
              <Input
                value={s.topbar_left}
                onChange={(v) => upd({ topbar_left: v })}
              />
            </Field>

            <Field label="Topbar Right Text">
              <Input
                value={s.topbar_right}
                onChange={(v) => upd({ topbar_right: v })}
              />
            </Field>
          </div>

          <div className="mt-6">
            <Field label="Footer Text">
              <Textarea
                rows={3}
                value={s.footer_text}
                onChange={(e) =>
                  upd({ footer_text: e.target.value })
                }
              />
            </Field>
          </div>
        </Card>

        {/* Social Links */}
        <Card
          title="Social Media Links"
          desc="Connect your social media platforms"
          icon={Globe}
        >
          <div className="space-y-4">
            {(s.social_links || []).map((l, i) => (
              <div
                key={l.id || i}
                className="grid lg:grid-cols-[180px_1fr_56px] gap-3"
              >
                <Input
                  value={l.platform}
                  onChange={(v) => {
                    const arr = [...s.social_links];
                    arr[i] = {
                      ...arr[i],
                      platform: v,
                    };

                    upd({ social_links: arr });
                  }}
                  placeholder="facebook"
                />

                <Input
                  value={l.url}
                  onChange={(v) => {
                    const arr = [...s.social_links];
                    arr[i] = {
                      ...arr[i],
                      url: v,
                    };

                    upd({ social_links: arr });
                  }}
                  placeholder="https://facebook.com/store"
                />

                <button
                  type="button"
                  onClick={() =>
                    upd({
                      social_links:
                        s.social_links.filter(
                          (_, k) => k !== i
                        ),
                    })
                  }
                  className="h-12 rounded-2xl bg-rose-500 hover:bg-rose-600 transition flex items-center justify-center text-white"
                >
                  <X size={16} />
                </button>
              </div>
            ))}

            <button
              type="button"
              onClick={() =>
                upd({
                  social_links: [
                    ...(s.social_links || []),
                    {
                      id: Date.now() + "",
                      platform: "",
                      url: "",
                    },
                  ],
                })
              }
              className="inline-flex items-center gap-2 h-12 px-5 rounded-2xl border-2 border-dashed border-emerald-300 text-emerald-600 hover:bg-emerald-50 transition font-semibold"
            >
              <Plus size={16} />
              Add Social Link
            </button>
          </div>
        </Card>

        {/* Homepage Blocks */}
        <Card
          title="Homepage Blocks"
          desc="Control homepage sections and ordering"
          icon={LayoutDashboard}
        >
          <div className="space-y-4">
            {(s.homepage_blocks || []).map((b, i) => (
              <div
                key={b.id}
                className="border border-slate-200 rounded-2xl p-5 bg-white"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
                  <div>
                    <div className="font-bold text-slate-900">
                      {b.label}
                    </div>

                    <div className="inline-flex mt-2 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold uppercase tracking-wide">
                      {b.type}
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Toggle
                      value={b.active}
                      onChange={(v) => {
                        const arr = [...s.homepage_blocks];

                        arr[i] = {
                          ...arr[i],
                          active: v,
                        };

                        upd({ homepage_blocks: arr });
                      }}
                    />

                    <button
                      onClick={() => {
                        if (i === 0) return;

                        const arr = [...s.homepage_blocks];

                        [arr[i - 1], arr[i]] = [
                          arr[i],
                          arr[i - 1],
                        ];

                        upd({ homepage_blocks: arr });
                      }}
                      className="h-10 px-4 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm font-semibold"
                    >
                      ↑
                    </button>

                    <button
                      onClick={() => {
                        if (
                          i ===
                          s.homepage_blocks.length - 1
                        )
                          return;

                        const arr = [...s.homepage_blocks];

                        [arr[i + 1], arr[i]] = [
                          arr[i],
                          arr[i + 1],
                        ];

                        upd({ homepage_blocks: arr });
                      }}
                      className="h-10 px-4 rounded-xl border border-slate-200 hover:bg-slate-50 text-sm font-semibold"
                    >
                      ↓
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Bottom Save Bar */}
        <div className="sticky bottom-5 z-50">
          <div className="bg-white/90 backdrop-blur-xl border border-slate-200 rounded-3xl shadow-2xl p-5 flex flex-col lg:flex-row items-center justify-between gap-4">
            <div>
              <div className="font-semibold text-slate-900">
                Ready to publish changes?
              </div>

              <div className="text-sm text-slate-500 mt-1">
                Save your store settings to apply updates.
              </div>
            </div>

            <button
              onClick={save}
              disabled={busy}
              className="inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-slate-900 hover:bg-black transition text-white font-bold disabled:opacity-60"
            >
              <Save size={16} />
              {busy ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}